import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import _ from 'lodash';
import { IOverlayLoaderStore } from '../../../../../../modules/common/OverlayLoader.store';
import { ACDLError, CancelledError, ForbiddenError } from '../../../../../models/error.model';
import { SortingDirection } from '../../../../../models/sortingDirection.model';
import { NotificationLevel, ToastNotification } from '../../../../notifications/models/notification.model';
import { INotificationsService } from '../../../../notifications/services/notifications.service';
import { EmptyAnnotationToolFilter, IImageFilters, ImageDefaultFilters, SortingBy } from '../imageFilters.model';
import { ImagesDefaultPaging } from '../projectDetailsImages.model';
import { IProjectDetailsImagesStore } from '../projectDetailsImages.store';
import { IGetImagesRequest, IImageFiltersApiService, ISaveFiltersRequest, IUpsertFiltersRequest } from './imageFiltersApi.service';

export interface IImageFilterService {
  setImageNameAsync: (imageName: string) => Promise<void>;
  applyAsync: (filters: IImageFilters) => Promise<void>;
  upsertFiltersAsync: (projectId: string, filters: IImageFilters) => Promise<string>;
  filterImages: () => Promise<void>;
  clearAllAsync: () => Promise<void>;
  clearStatusesAsync: () => Promise<void>;
  clearCorrectedAsync: () => Promise<void>;
  clearImageSetsAsync: () => Promise<void>;
  clearInDatasetsAsync: () => Promise<void>;
  clearReviewedByAsync: () => Promise<void>;
  clearAnnotatedByAsync: () => Promise<void>;
  clearAnnotationToolsAsync: () => Promise<void>;
  setDefaultImagePaging: () => Promise<void>;
  paginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
  changeSortingAsync: (by: SortingBy, direction: SortingDirection | undefined) => Promise<void>;
  saveFilterAsync: (name: string, filters: IImageFilters) => Promise<void>;
  deleteFilterAsync: (filterId: string) => Promise<void>;
  getFilterDataAsync: (projectId: string, filterId: string | undefined) => Promise<void>;
  changeSelectionMode: () => void;
}

export namespace IImageFilterService {
  export const $: interfaces.ServiceIdentifier<IImageFilterService> = Symbol('IMAGE_FILTER_SERVICE');
}

@injectable()
export class ImageFilterService implements IImageFilterService {
  constructor(
    @inject(IProjectDetailsImagesStore.$) private readonly store: IProjectDetailsImagesStore,
    @inject(IImageFiltersApiService.$) private readonly imageFiltersApiService: IImageFiltersApiService,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService
  ) {}

  getFilterDataAsync = async (projectId: string, filterId: string | undefined): Promise<void> => {
    const filtersResult = await this.imageFiltersApiService.getActiveFilters({ projectId, filterId });

    if (filtersResult instanceof ACDLError) {
      return;
    }

    this.store.setFilterData(filtersResult.availableFilters, filtersResult.activeFilter, filtersResult.savedFilters);
  };

  applyAsync = async (filters: IImageFilters): Promise<void> => {
    this.setFilters(filters);
    await this.filterImages();
  };

  upsertFiltersAsync = async (projectId: string, filters: IImageFilters): Promise<string> => {
    const upsertRequest: IUpsertFiltersRequest = {
      projectId,
      filters: {
        ...filters,
        annotationTools: filters.annotationTools.filter((x) => x.l1FilterType !== undefined),
        sorting: filters.sorting.filter((x) => x.by !== undefined),
      },
    };
    const result = await this.imageFiltersApiService.upsertFiltersAsync(upsertRequest);

    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  };

  clearAllAsync = async (): Promise<void> => {
    this.setFilters(ImageDefaultFilters);
    await this.filterImages();
  };

  setImageNameAsync = async (imageName: string): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      imageName,
    };

    this.setFilters(newFilters);

    await this.handleSearchChangeDebounced();
  };

  clearInDatasetsAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      datasets: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  setFilters = (imageFilters: IImageFilters): void => {
    this.store.setFilters(imageFilters);
    this.store.setSelectionMode('Select');
  };

  changeSelectionMode = (): void => {
    this.store.setSelectionMode(this.store.selectionMode === 'Select' ? 'Deselect' : 'Select');
  };

  clearAnnotationToolsAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      annotationTools: [{ ...EmptyAnnotationToolFilter }],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  clearAnnotatedByAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      annotatedBy: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  clearReviewedByAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      reviewedBy: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  clearStatusesAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      annotationStatuses: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  clearImageSetsAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      imageSets: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  clearCorrectedAsync = async (): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      corrected: [],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  paginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    this.store.setImagePaging({
      ...this.store.imagesPaging,
      pageNumber,
      pageSize,
    });
    await this.filterImages();
  };

  changeSortingAsync = async (by: SortingBy, direction: SortingDirection | undefined): Promise<void> => {
    const newFilters = {
      ...this.store.imageFilters,
      sorting: direction === undefined ? [] : [{ by, direction, order: 0 }],
    };

    this.setFilters(newFilters);
    await this.filterImages();
  };

  saveFilterAsync = async (name: string, filters: IImageFilters): Promise<void> => {
    const saveRequest: ISaveFiltersRequest = {
      name,
      projectId: this.store.projectId,
      filters: {
        ...filters,
        annotationTools: filters.annotationTools.filter((x) => x.l1FilterType !== undefined),
        sorting: filters.sorting.filter((x) => x.by !== undefined),
      },
    };

    this.overlayLoaderStore.enableLoader('filter-save');
    await this.imageFiltersApiService.saveFiltersAsync(saveRequest);
    const filtersResult = await this.imageFiltersApiService.getActiveFilters({ projectId: this.store.projectId, filterId: this.store.filterId });
    this.overlayLoaderStore.disableLoader('filter-save');

    if (filtersResult instanceof ACDLError) {
      return;
    }

    this.store.setFilterData(filtersResult.availableFilters, filtersResult.activeFilter, filtersResult.savedFilters);
  };

  deleteFilterAsync = async (filterId: string): Promise<void> => {
    this.overlayLoaderStore.enableLoader('filter-delete');
    await this.imageFiltersApiService.deleteFiltersAsync({ filterId });
    const filtersResult = await this.imageFiltersApiService.getActiveFilters({ projectId: this.store.projectId, filterId: this.store.filterId });
    this.overlayLoaderStore.disableLoader('filter-delete');

    await this.notifyAboutApiError(filtersResult, 'deleting_filter_failed');
    if (filtersResult instanceof ACDLError) {
      return;
    }

    this.store.setFilterData(filtersResult.availableFilters, filtersResult.activeFilter, filtersResult.savedFilters);
  };

  filterImages = async (): Promise<void> => {
    const request: IGetImagesRequest = {
      projectId: this.store.projectId,
      pageNumber: this.store.imagesPaging.pageNumber,
      pageSize: this.store.imagesPaging.pageSize,
      filters: {
        ...this.store.imageFilters,
        annotationTools: this.store.imageFilters.annotationTools.filter((x) => x.l1FilterType !== undefined),
      },
    };

    this.overlayLoaderStore.enableLoader('details-images-loading');
    const result = await this.imageFiltersApiService.getProjectImagesAsync(request);
    this.overlayLoaderStore.disableLoader('details-images-loading');

    await this.notifyAboutApiError(result, 'filtering_images_failed');
    if (result instanceof ACDLError) {
      return;
    }

    if (result.pagesCount < result.pageNumber) {
      this.store.setImagePaging({
        ...this.store.imagesPaging,
        pageNumber: result.pagesCount,
      });
      await this.filterImages();
    } else {
      this.store.updateImages(
        result.data,
        {
          ...this.store.imagesPaging,
          pageNumber: Math.max(result.pageNumber, 1),
          pagesCount: result.pagesCount,
          totalCount: result.totalCount,
          totalImagesCount: result.totalImagesCount,
          unlockedImagesCount: result.unlockedImagesCount,
        },
        result.filterId
      );
    }
  };

  setDefaultImagePaging = async (): Promise<void> => {
    this.store.setImagePaging({ ...ImagesDefaultPaging });

    if (this.store.projectId) {
      await this.filterImages();
    }
  };

  private notifyAboutApiError = async (result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> => {
    if (
      result instanceof ACDLError &&
      !(result instanceof ForbiddenError) &&
      !(result instanceof CancelledError) &&
      (!dontShowBadRequest || !result.isBadRequest())
    ) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  };

  private handleSearchChangeDebounced = _.debounce(async () => this.filterImages(), 500, { leading: false, trailing: true });
}
